import React, { useState, useEffect, useRef, useCallback } from "react";
import IframeBlockedMessage from "./IframeBlockedMessage";

const LOAD_TIMEOUT_MS = 8000;
const EMBED_CHECK_URL = "/api/v1/embed_check";

/**
 * Cache embed-check results for the lifetime of the page so repeated
 * renders of the same URL skip the network round-trip.
 */
const embedCheckCache = new Map();

/**
 * Wrapper around <iframe> that normalises cross-browser iframe failure
 * handling and always shows the same IframeBlockedMessage fallback UI
 * when an embed cannot be displayed (Chrome, Firefox, Safari, etc.).
 *
 * ─── Detection pipeline ──────────────────────────────────────────────
 *
 *  Signal 1 – Server-side header check  (most reliable, catches Chrome)
 *    A HEAD request to /api/v1/embed_check?url=… reads the target's
 *    X-Frame-Options and CSP frame-ancestors headers before the iframe
 *    loads. This is the only way to detect Chrome's "refused to connect"
 *    page, because Chrome renders it as a cross-origin document that
 *    fires onLoad normally and looks identical to a working embed from JS.
 *    The endpoint is public (no auth required) so it works in the viewer.
 *
 *  Signal 2 – onLoad + document probe  (Firefox / Safari blank frames)
 *    After onLoad fires, we try to read contentDocument. In Firefox and
 *    Safari, a blocked frame exposes an empty same-origin document (empty
 *    body & head). If we can read it and it is empty, the frame is blocked.
 *
 *    IMPORTANT: a cross-origin SecurityError means we CANNOT determine
 *    from client-side JS alone whether the frame is working or blocked
 *    (both throw the same error). In that case we defer to Signal 1 or
 *    the timeout to decide — we never optimistically set "loaded".
 *
 *  Signal 3 – onError  (network-level failures: DNS, TLS, unreachable)
 *
 *  Signal 4 – Timeout fallback  (8 s; anything that slips through above)
 *
 * ─── Loading spinner ─────────────────────────────────────────────────
 *  While detection is in progress an opaque overlay hides the raw iframe
 *  so the user never sees a browser-specific error flash, regardless of
 *  which browser they are using.
 */
const SafeIFrame = ({
  src,
  style = {},
  title,
  id,
  onClick,
  scrolling,
  loadTimeout = LOAD_TIMEOUT_MS,
  ...props
}) => {
  const [status, setStatus] = useState("loading");

  // Mirror of `status` in a ref so callbacks always read the latest value
  // without being stale closures (refs don't cause re-renders).
  const statusRef = useRef("loading");

  const iframeRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // Wrapper so statusRef and state are always kept in sync.
  const resolve = useCallback((newStatus) => {
    if (!mountedRef.current) return;
    // Never downgrade a definitive verdict.
    if (statusRef.current === "error" || statusRef.current === "loaded") return;
    statusRef.current = newStatus;
    setStatus(newStatus);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    statusRef.current = "loading";
    setStatus("loading");

    if (!src) {
      resolve("error");
      return;
    }

    // ── Signal 1: server-side header check ────────────────────────────
    const checkViaServer = async () => {
      if (embedCheckCache.has(src)) {
        const cached = embedCheckCache.get(src);
        if (!cached.embeddable) resolve("error");
        else resolve("loaded");
        return;
      }

      try {
        const resp = await fetch(
          `${EMBED_CHECK_URL}?url=${encodeURIComponent(src)}`,
          { signal: AbortSignal.timeout(5000) }
        );
        if (resp.ok) {
          const data = await resp.json();
          embedCheckCache.set(src, data);
          if (!data.embeddable) resolve("error");
          else resolve("loaded");
        }
        // Non-2xx (e.g. endpoint unavailable) → fall through to other signals
      } catch (_) {
        // Fetch failed or timed out → rely on client-side signals only
      }
    };

    checkViaServer();

    // ── Signal 4: timeout fallback ────────────────────────────────────
    timerRef.current = setTimeout(() => resolve("error"), loadTimeout);

    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [src, loadTimeout, resolve]);

  // ── Signal 2: onLoad + document probe ───────────────────────────────
  const handleLoad = useCallback(() => {
    // The server check or a previous signal already resolved — bail out.
    // This prevents Chrome's blocked-page onLoad from overriding "error".
    if (statusRef.current !== "loading") return;

    const iframe = iframeRef.current;
    if (!iframe) {
      // No ref — nothing to probe; server check / timeout will decide.
      return;
    }

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const bodyContent = (doc.body?.innerHTML || "").trim();
        const headContent = (doc.head?.innerHTML || "").trim();

        if (!bodyContent && !headContent) {
          // Empty same-origin document → Firefox/Safari blocked frame.
          clearTimeout(timerRef.current);
          resolve("error");
          return;
        }

        if (doc.URL === "about:blank" && src !== "about:blank") {
          clearTimeout(timerRef.current);
          resolve("error");
          return;
        }

        // Non-empty same-origin document → loaded successfully.
        clearTimeout(timerRef.current);
        resolve("loaded");
        return;
      }
    } catch (_) {
      // SecurityError — the frame is cross-origin.
      //
      // We CANNOT tell from this error alone whether the frame is a
      // working cross-origin embed (YouTube, Vimeo…) or Chrome's
      // "refused to connect" error page — both throw SecurityError.
      //
      // DO NOT call resolve("loaded") here.  Let Signal 1 (server check)
      // or Signal 4 (timeout) make the final call.
      return;
    }
  }, [src, resolve]);

  // ── Signal 3: onError ────────────────────────────────────────────────
  const handleError = useCallback(() => {
    clearTimeout(timerRef.current);
    resolve("error");
  }, [resolve]);

  const { width, height, ...iframeStyle } = style;

  if (status === "error") {
    return <IframeBlockedMessage style={{ width, height }} url={src} />;
  }

  return (
    <div style={{ position: "relative", width, height, overflow: "hidden" }}>
      {status === "loading" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            zIndex: 2,
          }}
        >
          <style>
            {`@keyframes safeiframe-spin { to { transform: rotate(360deg); } }`}
          </style>
          <div
            style={{
              width: 28,
              height: 28,
              border: "3px solid #e0e0e0",
              borderTopColor: "#888",
              borderRadius: "50%",
              animation: "safeiframe-spin 0.7s linear infinite",
            }}
          />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        style={{
          ...iframeStyle,
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
        }}
        title={title || "url"}
        id={id}
        scrolling={scrolling}
        onClick={onClick}
        frameBorder="0"
        allow="autoplay; fullscreen"
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </div>
  );
};

export default SafeIFrame;
